/**
 * CRM-LIST-VIEWS-V1 (V1-P7; rider R-16) -- the canonical leads-list contract,
 * FRONTEND side: filter/sort vocabularies (lockstep mirrors of the backend
 * one-home constants), the COLUMN REGISTRY (C-10), and the ROLE-DEFAULT VIEW
 * map (C-11). PURE: no React, no fetch, no Date.now() -- callers inject time.
 *
 * SECURITY POSTURE: nothing here is an access decision. The SERVER shapes
 * data and filter options (tier projection + C-6 role shaping on
 * GET /api/leads/workspace); this module only mirrors the lawful shapes so
 * the UI never OFFERS an option the server would reject. Column lawfulness
 * is double-fenced: the static tier map below AND the per-record
 * `visibleSections` manifest the wire actually carries -- a URL-injected
 * unauthorized column id is dropped at normalization AND its data is
 * structurally absent server-side (C-14 pin 4).
 *
 * LOCKSTEP MIRRORS (the established separate-packages convention; kept in
 * lockstep by review + the shared-vocabulary pins in
 * frontend/src/dashboard/__tests__/lead-list-contract.test.ts):
 *   - stage tokens/labels <- lib/domain/lead/lead-lifecycle.ts LEAD_STAGES +
 *     STAGE_CONTRACTS.outcome
 *   - sla tokens <- lib/sla-leakage/sla-leakage-engine.ts SLA_BREACH_CLASSES
 *     (labels reuse THE existing classMeta, sla-leakage-view.ts -- no clone)
 *   - source tokens <- lib/lead-capture-handler.ts LEAD_CAPTURE_SOURCES
 *     (identical to the leads.source_normalized CHECK)
 *   - sort keys <- lib/lead-list/lead-list-contract.ts LEAD_LIST_SORTS
 */

import type { Lead360Section } from '@/dashboard/owner/lead-360-view';
import {
  classMeta,
  SLA_CLASS_ORDER,
  type SlaBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';

// ---- Roles on this surface (the workspace audience) ------------------------

/** The workspace READ audience (mirror of LEADS_WORKSPACE_ROLES,
 *  api/routes/leads-workspace.ts). MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1
 *  (founder ruling 2026-07-28) added gm_agm + regional_head to that set, so
 *  they are mirrored here too. WHY THIS MIRROR MUST TRACK THE BACKEND: this
 *  union is the input to `isLeadListRole`, and LeadsWorkspacePage falls back
 *  to 'receptionist' for any role outside it -- so leaving the managers out
 *  would have served them a FRONT-DESK list shape (columns, views, filters)
 *  while the server sent them the leadership tier. That is an under-render,
 *  never a leak (the wire's `visibleSections` manifest is the runtime half),
 *  but it is still a wrong surface. */
export type LeadListRole =
  | 'owner'
  | 'coc'
  | 'receptionist'
  | 'call_center_agent'
  | 'gm_agm'
  | 'regional_head';

const LEAD_LIST_ROLES: readonly LeadListRole[] = [
  'owner',
  'coc',
  'receptionist',
  'call_center_agent',
  'gm_agm',
  'regional_head',
];

export function isLeadListRole(v: string | null | undefined): v is LeadListRole {
  return v !== null && v !== undefined && (LEAD_LIST_ROLES as readonly string[]).includes(v);
}

/**
 * THE SEE-NOT-DO MIRROR (MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1).
 *
 * FE mirror of LEADS_WORKSPACE_ACTION_ROLES (api/routes/leads-workspace.ts):
 * the roles whose WRITES this surface's own gate admits. Read membership is
 * NOT action membership -- `gm_agm` / `regional_head` read this surface and
 * hold no action through it, so a control gated on the surface's own audience
 * must consult THIS set, never `isLeadListRole` / `leadsWorkspaceEnabled`.
 *
 * A UI REDUCTION ONLY. `requireLeadsWorkspaceActionScope` on the server is the
 * boundary; this exists so no door is drawn that cannot open (LES-016). It
 * governs ONLY controls gated by the workspace audience itself -- assignment,
 * appointment, patient-bridge, and contact-reveal controls keep their OWN
 * pre-existing role mirrors and are untouched here.
 */
const LEAD_LIST_ACTION_ROLES: readonly string[] = [
  'owner',
  'coc',
  'receptionist',
  'call_center_agent',
];

export function canActOnLeadsWorkspace(role: string | null | undefined): boolean {
  return role !== null && role !== undefined && LEAD_LIST_ACTION_ROLES.includes(role);
}

/** FE mirror of lead360TierForRole (lib/domain/lead/lead-360-role-view.ts)
 *  for the audience roles only -- drives the STATIC half of column/filter
 *  lawfulness (the runtime half is the wire's visibleSections manifest). */
export type LeadListTier = 'leadership' | 'manager' | 'front_desk';

export function leadListTierForRole(role: LeadListRole): LeadListTier {
  switch (role) {
    case 'owner':
    // MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1: verbatim mirror of the backend
    // FQ-5 mapping (lib/domain/lead/lead-360-role-view.ts) -- both manager
    // roles are the LEADERSHIP tier, the owner lens. Not a new projection:
    // the backend mapping predates the ruling and was reused by it.
    case 'gm_agm':
    case 'regional_head':
      return 'leadership';
    case 'coc':
      return 'manager';
    case 'receptionist':
    case 'call_center_agent':
    default:
      return 'front_desk';
  }
}

// ---- Stage vocabulary (lifecycle verbatim; labels = STAGE_CONTRACTS.outcome)

export type LeadListStage =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'appointment_booked'
  | 'arrived'
  | 'patient'
  | 'converted_to_revenue'
  | 'lost';

export const LEAD_LIST_STAGES: ReadonlyArray<{
  readonly token: LeadListStage;
  readonly label: string;
}> = [
  { token: 'new', label: 'New lead' },
  { token: 'contacted', label: 'Contacted' },
  { token: 'qualified', label: 'Qualified' },
  { token: 'appointment_booked', label: 'Appointment booked' },
  { token: 'arrived', label: 'Arrived' },
  { token: 'patient', label: 'Patient' },
  { token: 'converted_to_revenue', label: 'Converted to revenue' },
  { token: 'lost', label: 'Lost' },
];

// ---- SLA vocabulary (classifier verbatim; labels reuse classMeta) ----------

export const LEAD_LIST_SLA_CLASSES: ReadonlyArray<{
  readonly token: SlaBreachClass;
  readonly label: string;
}> = SLA_CLASS_ORDER.map((token) => ({ token, label: classMeta(token).title }));

// ---- Source vocabulary (canonical capture set; humanized labels) -----------

export type LeadListSource =
  | 'paid_search'
  | 'paid_social'
  | 'organic_search'
  | 'directory'
  | 'social_organic'
  | 'offline'
  | 'walk_in'
  | 'corporate'
  | 'insurance'
  | 'referral'
  | 'campaign'
  | 'whatsapp'
  | 'webchat'
  | 'other';

export const LEAD_LIST_SOURCES: ReadonlyArray<{
  readonly token: LeadListSource;
  readonly label: string;
}> = [
  { token: 'paid_search', label: 'Paid search' },
  { token: 'paid_social', label: 'Paid social' },
  { token: 'organic_search', label: 'Organic search' },
  { token: 'directory', label: 'Directory' },
  { token: 'social_organic', label: 'Social organic' },
  { token: 'offline', label: 'Offline' },
  { token: 'walk_in', label: 'Walk-in' },
  { token: 'corporate', label: 'Corporate' },
  { token: 'insurance', label: 'Insurance' },
  { token: 'referral', label: 'Referral' },
  { token: 'campaign', label: 'Campaign' },
  { token: 'whatsapp', label: 'WhatsApp' },
  { token: 'webchat', label: 'Web chat' },
  { token: 'other', label: 'Other' },
];

// ---- Sort vocabulary (server-validated keys) --------------------------------

export type LeadListSortKey = 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc';

export const LEAD_LIST_SORTS: ReadonlyArray<{
  readonly key: LeadListSortKey;
  readonly label: string;
}> = [
  { key: 'created_desc', label: 'Newest first' },
  { key: 'created_asc', label: 'Oldest first' },
  { key: 'name_asc', label: 'Name A-Z' },
  { key: 'name_desc', label: 'Name Z-A' },
];

export const LEAD_LIST_DEFAULT_SORT: LeadListSortKey = 'created_desc';

// ---- Age filter presets (LEAD AGE = created_at age; C-5) --------------------

export const LEAD_LIST_AGE_PRESETS: ReadonlyArray<{
  readonly hours: number;
  readonly label: string;
}> = [
  { hours: 2, label: 'Older than 2 hours' },
  { hours: 24, label: 'Older than 1 day' },
  { hours: 72, label: 'Older than 3 days' },
  { hours: 168, label: 'Older than 7 days' },
  { hours: 720, label: 'Older than 30 days' },
];

export const LEAD_LIST_MAX_AGE_HOURS = 8760;

// ---- Filter state (the URL-backed view state) -------------------------------

export interface LeadListFilterState {
  readonly stages: readonly LeadListStage[];
  readonly slaClasses: readonly SlaBreachClass[];
  /** 'me' | 'unassigned' | a staff id string (from the server's options). */
  readonly assignee: string | null;
  readonly sources: readonly LeadListSource[];
  readonly olderThanHours: number | null;
  readonly noContact: boolean;
  readonly callbackOverdue: boolean;
  readonly linkPending: boolean;
}

export const EMPTY_LEAD_LIST_FILTERS: LeadListFilterState = Object.freeze({
  stages: [],
  slaClasses: [],
  assignee: null,
  sources: [],
  olderThanHours: null,
  noContact: false,
  callbackOverdue: false,
  linkPending: false,
});

export interface LeadListViewState {
  readonly view: string | null;
  readonly filters: LeadListFilterState;
  readonly sort: LeadListSortKey;
  /** Visible column ids (registry order), or null = the role default. */
  readonly columns: readonly string[] | null;
}

// ---- COLUMN REGISTRY (C-10) -------------------------------------------------

/**
 * One shared registry: id / label / wire dependency (section) / roles /
 * sortability / default visibility / formatting / sensitivity class.
 * `section: 'core'` fields ride every tier's wire (ids, clinic, created,
 * the ownership overlay); tier-gated sections mirror
 * LEAD_360_TIER_SECTIONS -- a column whose section a tier does not receive
 * is ABSENT from that role's chooser AND cannot be resurrected by URL
 * (normalizeColumnsForRole) AND its data is absent server-side.
 */
export interface LeadListColumnDef {
  readonly id: string;
  readonly label: string;
  /** Wire dependency: 'core' or the Lead360Section the field rides. */
  readonly section: 'core' | Lead360Section;
  /** Sort keys this column header toggles, when server-sortable. */
  readonly sort?: { readonly asc: LeadListSortKey; readonly desc: LeadListSortKey };
  /** Sensitivity class (no clinical column exists on this wire by
   *  construction -- the clinical fence is upstream at the SELECT). */
  readonly sensitivity: 'administrative' | 'attribution';
  /** Roles where the column is ON by default (C-11; all roles may add any
   *  LAWFUL column via the chooser). */
  readonly defaultOn: readonly LeadListRole[];
}

const ALL_ROLES: readonly LeadListRole[] = LEAD_LIST_ROLES;

export const LEAD_LIST_COLUMNS: readonly LeadListColumnDef[] = [
  {
    id: 'lead',
    label: 'Lead',
    section: 'core',
    sensitivity: 'administrative',
    defaultOn: ALL_ROLES,
  },
  {
    id: 'name',
    label: 'Name',
    section: 'identity',
    sort: { asc: 'name_asc', desc: 'name_desc' },
    sensitivity: 'administrative',
    defaultOn: ALL_ROLES,
  },
  {
    id: 'stage',
    label: 'Stage',
    section: 'highlights',
    sensitivity: 'administrative',
    defaultOn: ALL_ROLES,
  },
  {
    id: 'sla',
    label: 'SLA',
    section: 'highlights',
    sensitivity: 'administrative',
    // MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1: the manager lens mirrors the
    // OWNER default (leadership tier). No existing role's default moved.
    defaultOn: ['owner', 'coc', 'call_center_agent', 'gm_agm', 'regional_head'],
  },
  {
    id: 'clinic',
    label: 'Clinic',
    section: 'core',
    sensitivity: 'administrative',
    defaultOn: ALL_ROLES,
  },
  {
    id: 'source',
    label: 'Source',
    section: 'source',
    sensitivity: 'attribution',
    // MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1: lawful for the leadership tier
    // (FQ-2 -- marketing source IS part of the management read per the
    // founder ruling's field-tier item), so it is ON by default for both
    // manager roles, mirroring owner. The FRONT-DESK fence is untouched:
    // 'source' is absent from the front_desk tier sections, so a receptionist
    // / agent still cannot see OR choose this column (FQ-1).
    defaultOn: ['owner', 'coc', 'gm_agm', 'regional_head'],
  },
  {
    // "Assigned to", never the OWNER role word (rider R-53; the station's
    // banked vocabulary).
    id: 'assigned',
    label: 'Assigned to',
    section: 'core',
    sensitivity: 'administrative',
    defaultOn: ALL_ROLES,
  },
  {
    id: 'latest_activity',
    label: 'Latest activity',
    section: 'activity',
    sensitivity: 'administrative',
    defaultOn: ['coc', 'receptionist', 'call_center_agent'],
  },
  {
    id: 'telephony',
    label: 'Telephony',
    section: 'linkage',
    sensitivity: 'administrative',
    defaultOn: [],
  },
  {
    id: 'walkin',
    label: 'Walk-in',
    section: 'linkage',
    sensitivity: 'administrative',
    defaultOn: [],
  },
  {
    id: 'created',
    label: 'Created',
    section: 'core',
    sort: { asc: 'created_asc', desc: 'created_desc' },
    sensitivity: 'administrative',
    defaultOn: ALL_ROLES,
  },
];

/** FE mirror of LEAD_360_TIER_SECTIONS (lib/domain/lead/lead-360-role-view.ts)
 *  restricted to the sections the registry uses. front_desk deliberately
 *  lacks 'source' (FQ-1). */
const TIER_SECTIONS: Readonly<Record<LeadListTier, readonly (Lead360Section | 'core')[]>> = {
  leadership: ['core', 'identity', 'highlights', 'source', 'activity', 'linkage'],
  manager: ['core', 'identity', 'highlights', 'source', 'activity', 'linkage'],
  front_desk: ['core', 'identity', 'highlights', 'activity', 'linkage'],
};

/** The columns a role may LAWFULLY see/choose (static tier half). */
export function lawfulColumnsForRole(role: LeadListRole): readonly LeadListColumnDef[] {
  const sections = TIER_SECTIONS[leadListTierForRole(role)];
  return LEAD_LIST_COLUMNS.filter((c) => sections.includes(c.section));
}

/** The role's DEFAULT visible column ids, registry order (C-11). */
export function defaultColumnsForRole(role: LeadListRole): readonly string[] {
  return lawfulColumnsForRole(role)
    .filter((c) => c.defaultOn.includes(role))
    .map((c) => c.id);
}

/**
 * Role-aware column normalization (C-9/C-10): keep only known, lawful ids,
 * registry order, de-duplicated; empty -> null (= role default). An
 * unauthorized or unknown id injected through the URL is DROPPED here (and
 * its data is absent from the wire regardless).
 */
export function normalizeColumnsForRole(
  role: LeadListRole,
  requested: readonly string[] | null,
): readonly string[] | null {
  if (requested === null || requested.length === 0) return null;
  const lawful = lawfulColumnsForRole(role).map((c) => c.id);
  const kept = lawful.filter((id) => requested.includes(id));
  if (kept.length === 0) return null;
  const defaults = defaultColumnsForRole(role);
  if (kept.length === defaults.length && kept.every((id, i) => id === defaults[i])) {
    // Canonical form: the role default serializes as ABSENT (one URL per state).
    return null;
  }
  return kept;
}

// ---- ROLE-DEFAULT VIEWS (C-11 -- the machine-readable map) ------------------

/**
 * One named view = a canonical filter/sort preset (never a second predicate
 * home: every entry composes the server contract's tokens verbatim).
 * `rationale` names the source of the default per C-11: tenant / founder /
 * provisional (founder confirms at PR review).
 */
export interface LeadListViewDef {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly filters: LeadListFilterState;
  readonly sort: LeadListSortKey;
  readonly rationale: 'tenant' | 'founder' | 'provisional';
  /**
   * LEADS-LIST-SALESFORCE-VIEWS-V1: a CLIENT-side view kind. `undefined` =
   * an ordinary server-composed preset (the C-11 default). `'recency'` = the
   * Salesforce "Recently viewed" view: its server READ is the base fenced
   * read (empty filters -- never a new read, never a fence change; the
   * server treats `view` as an echo-only token, behavior derives from
   * filters), but its ROWS are narrowed CLIENT-side to the records the
   * caller lawfully opened, ordered by open-recency. Because its filter/sort
   * shape COLLIDES with the base "All"/"My leads" read, its identity cannot
   * be derived from filters -- it is carried explicitly in the view id and
   * resolved by `resolveActiveViewId` / `isClientRecencyView`.
   */
  readonly client?: 'recency';
}

const VIEW_ALL: LeadListViewDef = {
  id: 'all',
  label: 'All leads',
  hint: 'Every lead in your verified scope, newest first.',
  filters: EMPTY_LEAD_LIST_FILTERS,
  sort: 'created_desc',
  rationale: 'founder',
};

const VIEW_MY_LEADS: LeadListViewDef = {
  id: 'my_leads',
  label: 'My leads',
  hint: 'Leads assigned to you, newest first.',
  filters: { ...EMPTY_LEAD_LIST_FILTERS, assignee: 'me' },
  sort: 'created_desc',
  rationale: 'provisional',
};

const VIEW_UNASSIGNED: LeadListViewDef = {
  id: 'unassigned',
  label: 'Unassigned',
  hint: 'Pool leads with no owner yet.',
  filters: { ...EMPTY_LEAD_LIST_FILTERS, assignee: 'unassigned' },
  sort: 'created_desc',
  rationale: 'provisional',
};

const VIEW_NO_CONTACT: LeadListViewDef = {
  id: 'no_contact',
  label: 'No contact logged',
  hint: 'No contact touch logged yet. A call may still have happened - event logging is manual.',
  filters: { ...EMPTY_LEAD_LIST_FILTERS, noContact: true },
  sort: 'created_asc',
  rationale: 'provisional',
};

const VIEW_CALLBACK_OVERDUE: LeadListViewDef = {
  id: 'callback_overdue',
  label: 'Callback overdue',
  hint: 'A promised callback instant has passed (the station callback rule).',
  filters: { ...EMPTY_LEAD_LIST_FILTERS, callbackOverdue: true },
  sort: 'created_asc',
  rationale: 'provisional',
};

const VIEW_SLA_BREACHED: LeadListViewDef = {
  id: 'sla_breached',
  label: 'SLA breached',
  hint: 'Breaching at least one leakage class (the V4-P0 classifier).',
  filters: {
    ...EMPTY_LEAD_LIST_FILTERS,
    slaClasses: SLA_CLASS_ORDER,
  },
  sort: 'created_asc',
  rationale: 'provisional',
};

const VIEW_LINK_PENDING: LeadListViewDef = {
  id: 'link_pending',
  label: 'Converted - link pending',
  hint: 'Converted signal recorded but no patient link yet (identity-repair work).',
  filters: { ...EMPTY_LEAD_LIST_FILTERS, linkPending: true },
  sort: 'created_asc',
  rationale: 'provisional',
};

const VIEW_DESK_NEW_BOOKED: LeadListViewDef = {
  id: 'desk_new_booked',
  label: 'New + booked',
  hint: 'New enquiries and booked appointments for your clinic, newest first.',
  filters: {
    ...EMPTY_LEAD_LIST_FILTERS,
    stages: ['new', 'appointment_booked'],
  },
  sort: 'created_desc',
  rationale: 'provisional',
};

/**
 * LEADS-LIST-SALESFORCE-VIEWS-V1 -- the Salesforce "Recently viewed" view.
 * CLIENT-side recency over the records the caller lawfully OPENED from this
 * list (quick view or the full-record door). The server read is the BASE
 * fenced read (EMPTY filters -- byte-identical to today's broadest lawful
 * read for the role; NEVER a new read, NEVER a fence widen); the rows are
 * intersected client-side with the recency memory and ordered by open time.
 * FENCE PIN: it can only ever contain records the fence already served, so
 * an ownership-fenced agent's Recently-viewed can never surface a foreign
 * lead (the current fenced page never contains one; the intersection double-
 * guards a crafted id in client storage).
 */
const VIEW_RECENTLY_VIEWED: LeadListViewDef = {
  id: 'recently_viewed',
  label: 'Recently viewed',
  hint: 'Leads you opened from this list, most recent first (this browser tab).',
  filters: EMPTY_LEAD_LIST_FILTERS,
  sort: 'created_desc',
  rationale: 'provisional',
  client: 'recency',
};

/**
 * The C-11 role-default map. FIRST entry = the role's DEFAULT view (the
 * role lands on its useful list with zero configuration -- E-2). All
 * defaults are PROVISIONAL pending founder confirmation at PR review
 * (recorded in the run record); no new org config key is created (C-11:
 * repo truth owns none today -- config-backing is the named seam).
 */
export const LEAD_LIST_ROLE_VIEWS: Readonly<
  Record<LeadListRole, readonly LeadListViewDef[]>
> = {
  // LEADS-LIST-SALESFORCE-VIEWS-V1: every role's EXISTING server-composed
  // views are preserved BYTE-IDENTICALLY (their reads are LAW -- the
  // adversarial "manager views byte-identical to today's reads" pin); the
  // Salesforce "Recently viewed" client view is APPENDED (never a new server
  // read, never a fence change). The DEFAULT view (index 0) is unchanged.
  owner: [
    VIEW_ALL,
    VIEW_UNASSIGNED,
    VIEW_NO_CONTACT,
    VIEW_SLA_BREACHED,
    VIEW_LINK_PENDING,
    VIEW_RECENTLY_VIEWED,
  ],
  coc: [
    VIEW_MY_LEADS,
    VIEW_UNASSIGNED,
    VIEW_CALLBACK_OVERDUE,
    VIEW_NO_CONTACT,
    VIEW_SLA_BREACHED,
    VIEW_LINK_PENDING,
    VIEW_ALL,
    VIEW_RECENTLY_VIEWED,
  ],
  receptionist: [
    VIEW_DESK_NEW_BOOKED,
    VIEW_MY_LEADS,
    VIEW_UNASSIGNED,
    VIEW_NO_CONTACT,
    VIEW_ALL,
    VIEW_RECENTLY_VIEWED,
  ],
  // CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1 (founder-ratified
  // manager-controlled assignment): the agent's ONLY lawful universe is its
  // own assigned leads, so "All leads" and "Unassigned" are REMOVED from the
  // agent's view set (LES-016: role-shaped or absent). This is CLARITY,
  // not the control -- the server ownership fence
  // (api/routes/leads-workspace.ts LEAD_OWNERSHIP_FENCED_ROLES) is the
  // security boundary; a URL-injected view/filter still returns only the
  // agent's own assigned leads. "Recently viewed" is client-side recency
  // over rows the fence ALREADY served, so it cannot widen that universe.
  call_center_agent: [VIEW_MY_LEADS, VIEW_CALLBACK_OVERDUE, VIEW_NO_CONTACT, VIEW_RECENTLY_VIEWED],
  /*
   * MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1 (founder ruling 2026-07-28).
   * The manager lens is SEE-NOT-DO, so both sets are the OWNER set verbatim
   * -- the supervisory read (All / Unassigned / No contact / SLA breached /
   * Converted-link-pending) with the same default view (All leads, index 0).
   * "My leads" is DELIBERATELY absent: a manager is not an assignee tier on
   * this surface (they hold assign-others authority, not self-assign), so the
   * view would render an honest but useless empty for them (LES-016).
   * NO NEW READ: every entry composes tokens the server contract already
   * validates, and each resolves to a read the manager's clinic fence already
   * bounds -- the view set is presentation, never a fence.
   */
  gm_agm: [
    VIEW_ALL,
    VIEW_UNASSIGNED,
    VIEW_NO_CONTACT,
    VIEW_SLA_BREACHED,
    VIEW_LINK_PENDING,
    VIEW_RECENTLY_VIEWED,
  ],
  regional_head: [
    VIEW_ALL,
    VIEW_UNASSIGNED,
    VIEW_NO_CONTACT,
    VIEW_SLA_BREACHED,
    VIEW_LINK_PENDING,
    VIEW_RECENTLY_VIEWED,
  ],
};

export function viewsForRole(role: LeadListRole): readonly LeadListViewDef[] {
  return LEAD_LIST_ROLE_VIEWS[role];
}

export function defaultViewForRole(role: LeadListRole): LeadListViewDef {
  return LEAD_LIST_ROLE_VIEWS[role][0];
}

export function viewByIdForRole(
  role: LeadListRole,
  id: string | null,
): LeadListViewDef | null {
  if (id === null) return null;
  return viewsForRole(role).find((v) => v.id === id) ?? null;
}

/** True when a view id is the CLIENT-recency ("Recently viewed") view for
 *  the given role. Client views collide with the base read on filters/sort,
 *  so their identity is carried explicitly, never derived. */
export function isClientRecencyView(role: LeadListRole, id: string | null): boolean {
  if (id === null) return false;
  const def = viewByIdForRole(role, id);
  return def !== null && def.client === 'recency';
}

/**
 * Resolve the ACTIVE view id for a state (LEADS-LIST-SALESFORCE-VIEWS-V1).
 * An explicit CLIENT-recency view id in the state wins (it cannot be derived
 * from filters, which it shares with the base "All"/"My leads" read);
 * otherwise fall back to the exact-preset match (`stateMatchesView`), which
 * returns the first ordinary preset whose filter/sort shape the state
 * equals, or null for a custom state. Pure; test-pinned.
 */
export function resolveActiveViewId(
  role: LeadListRole,
  state: { view: string | null; filters: LeadListFilterState; sort: LeadListSortKey },
): string | null {
  if (state.view !== null && isClientRecencyView(role, state.view)) {
    return state.view;
  }
  const match = viewsForRole(role).find(
    (v) => v.client === undefined && stateMatchesView(state, v),
  );
  return match?.id ?? null;
}

/** True when the current state EXACTLY matches a view's preset (drives the
 *  active-chip render; a modified preset renders as a custom state). */
export function stateMatchesView(
  state: { filters: LeadListFilterState; sort: LeadListSortKey },
  view: LeadListViewDef,
): boolean {
  const a = state.filters;
  const b = view.filters;
  return (
    state.sort === view.sort &&
    a.assignee === b.assignee &&
    a.olderThanHours === b.olderThanHours &&
    a.noContact === b.noContact &&
    a.callbackOverdue === b.callbackOverdue &&
    a.linkPending === b.linkPending &&
    a.stages.length === b.stages.length &&
    a.stages.every((s) => b.stages.includes(s)) &&
    a.slaClasses.length === b.slaClasses.length &&
    a.slaClasses.every((s) => b.slaClasses.includes(s)) &&
    a.sources.length === b.sources.length &&
    a.sources.every((s) => b.sources.includes(s))
  );
}

// ---- Active-filter chips (C-13: removable, labeled fields) ------------------

export interface ActiveFilterChip {
  readonly key: string;
  readonly label: string;
}

/** Labeled, removable chips for the active filter state (fields, not
 *  sentences). `key` round-trips to the removal handler. */
export function activeFilterChips(filters: LeadListFilterState): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  for (const s of filters.stages) {
    const def = LEAD_LIST_STAGES.find((d) => d.token === s);
    chips.push({ key: `stage:${s}`, label: `Stage: ${def?.label ?? s}` });
  }
  for (const c of filters.slaClasses) {
    const def = LEAD_LIST_SLA_CLASSES.find((d) => d.token === c);
    chips.push({ key: `sla:${c}`, label: `SLA: ${def?.label ?? c}` });
  }
  if (filters.assignee !== null) {
    const label =
      filters.assignee === 'me'
        ? 'Assigned to: Me'
        : filters.assignee === 'unassigned'
          ? 'Assigned to: Unassigned'
          : `Assigned to: Staff #${filters.assignee}`;
    chips.push({ key: 'assignee', label });
  }
  for (const s of filters.sources) {
    const def = LEAD_LIST_SOURCES.find((d) => d.token === s);
    chips.push({ key: `source:${s}`, label: `Source: ${def?.label ?? s}` });
  }
  if (filters.olderThanHours !== null) {
    const preset = LEAD_LIST_AGE_PRESETS.find((p) => p.hours === filters.olderThanHours);
    chips.push({
      key: 'olderThanHours',
      label: preset?.label ?? `Older than ${filters.olderThanHours} hours`,
    });
  }
  if (filters.noContact) chips.push({ key: 'noContact', label: 'No contact logged' });
  if (filters.callbackOverdue) {
    chips.push({ key: 'callbackOverdue', label: 'Callback overdue' });
  }
  if (filters.linkPending) {
    chips.push({ key: 'linkPending', label: 'Converted - link pending' });
  }
  return chips;
}

/** Remove ONE active chip from the state (pure). */
export function removeFilterChip(
  filters: LeadListFilterState,
  chipKey: string,
): LeadListFilterState {
  if (chipKey.startsWith('stage:')) {
    const token = chipKey.slice('stage:'.length);
    return { ...filters, stages: filters.stages.filter((s) => s !== token) };
  }
  if (chipKey.startsWith('sla:')) {
    const token = chipKey.slice('sla:'.length);
    return { ...filters, slaClasses: filters.slaClasses.filter((s) => s !== token) };
  }
  if (chipKey.startsWith('source:')) {
    const token = chipKey.slice('source:'.length);
    return { ...filters, sources: filters.sources.filter((s) => s !== token) };
  }
  switch (chipKey) {
    case 'assignee':
      return { ...filters, assignee: null };
    case 'olderThanHours':
      return { ...filters, olderThanHours: null };
    case 'noContact':
      return { ...filters, noContact: false };
    case 'callbackOverdue':
      return { ...filters, callbackOverdue: false };
    case 'linkPending':
      return { ...filters, linkPending: false };
    default:
      return filters;
  }
}
